/**
 * Settings > Desk > HR approvers. Who may approve or decline an Employee exit
 * ticket. Add a person from the directory, or remove one. Every change saves
 * at once. At least one approver must stay, because an exit ticket cannot move
 * without one. A ticket already waiting keeps the approvers it was filed with.
 */

import { FilterLabel } from '@/components/FilterLabel';
import { useApprovers, usePutApprovers } from '@/lib/useServiceDesk';
import { PersonPicker } from './PersonPicker';
import { SdError } from './SdShell';
import { SD_BTN_GHOST, SD_CARD, SD_MUTED } from './sdStyles';

const PURPOSE = 'hr_exit';

export function ApproversSection() {
  const q = useApprovers();
  const put = usePutApprovers();
  const list = q.data?.[PURPOSE] ?? [];
  const emails = list.map((a) => a.email.toLowerCase());

  const save = (next: string[]) => put.mutate({ purpose: PURPOSE, emails: next });

  return (
    <section className={`${SD_CARD} mt-4`} aria-label="HR approvers">
      <div className="flex max-w-2xl flex-col gap-3">
        <div>
          <FilterLabel as="div">HR approvers for Employee exit tickets</FilterLabel>
          <p className={`text-[13px] ${SD_MUTED}`}>
            An Employee exit ticket waits in Pending HR approval until one of these people approves or declines it.
            The first decision counts. A ticket that is already waiting keeps the approvers it was filed with.
          </p>
        </div>
        {q.isLoading ? (
          <p className={`text-sm ${SD_MUTED}`}>Loading approvers...</p>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {list.map((a) => (
              <li
                key={a.email}
                className="flex items-center justify-between gap-3 rounded-md border border-amp-line px-3 py-1.5 text-[13px] dark:border-amp-dark-line"
              >
                <span className="min-w-0">
                  <span className="text-amp-ink dark:text-amp-dark-ink">{a.name ?? a.email}</span>
                  <span className={`ml-2 text-[12px] ${SD_MUTED}`}>{a.email}</span>
                </span>
                <button
                  type="button"
                  className={SD_BTN_GHOST}
                  disabled={put.isPending || list.length <= 1}
                  title={list.length <= 1 ? 'Keep at least one approver' : undefined}
                  onClick={() => save(emails.filter((e) => e !== a.email.toLowerCase()))}
                  aria-label={`Remove ${a.name ?? a.email}`}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="max-w-sm">
          <PersonPicker
            placeholder="Add an approver from the directory"
            onPick={(p) => {
              const email = p.email?.toLowerCase();
              if (email && !emails.includes(email)) save([...emails, email]);
            }}
          />
        </div>
        <SdError error={q.error ?? put.error} what="save the approvers" />
      </div>
    </section>
  );
}
